import { useEffect, useMemo, useState } from 'react'

import { useSelector } from 'react-redux'
import { faCheck, faMerge, faSave, faXmark } from '@fortawesome/pro-solid-svg-icons'
import { values } from 'lodash'

import { selectTranslate } from '../../../reducers/LocaleReducer/LocalReducer'
import IconButton from '../../antd/Buttons/IconButton'
import ConfirmableIconButton from '../../antd/Buttons/ConfirmableIconButton'
import SchoolsList from '../Lists/SchoolsList'
import SchoolCard from './SchoolCard'
import SchoolForm from '../../../Providers/Forms/InternshipRequestPortal/School/SchoolForm'
import { findSimilarSchools, School, SchoolProperties, SchoolValidators } from '../../../utils/entities/school'
import { isValid } from '../../../utils/validators'
import { getIdOrNull, validateString } from '../../../utils'

const { NAME } = SchoolProperties

const SchoolValidator = ({ countries = [], disabled = false, editable = false, labels, school: proposedSchool, schools = [], onMerge, onValidate }) => {
  const t = useSelector(selectTranslate)

  const [selectedSchool, setSelectedSchool] = useState(null)
  const [school, setSchool] = useState(proposedSchool)
  const [draft, setDraft] = useState(null)
  const [errorFields, setErrorFields] = useState([])

  const editing = draft !== null

  const schoolId = getIdOrNull(school)
  const isNamed = validateString(school?.[NAME], { notEmpty: true }).valid

  const duplicate = useMemo(
    () => findSimilarSchools(school, schools).find(existing => existing.id !== schoolId) ?? null,
    [school, schoolId, schools]
  )

  useEffect(() => {
    setSelectedSchool(duplicate?.id ?? null)
  }, [duplicate?.id])

  const cancelEdit = () => {
    setDraft(null)
    setErrorFields([])
  }

  const applyEdit = () => {
    const edited = new School(draft).trimmed()
    const errors = isValid(edited, values(SchoolValidators))

    setErrorFields(errors.keys)

    if (errors.keys.length === 0) {
      setSchool(new School(edited))
      setDraft(null)
    }
  }

  return (
    <div className='h-100 w-100 flex-row'>
      <div className='flex-column h-100 w-50 mr-8 jc-between'>
        <h3>{t(labels.title)}</h3>
        {editing && (
          <>
            <SchoolForm countries={countries} data={draft} errorFields={errorFields} onChange={setDraft} />
            <div className='flex-row w-100 j-ctn-e'>
              <IconButton className='mr-8' icon={faXmark} text={t('actions.cancel')} onClick={cancelEdit} />
              <IconButton icon={faCheck} text={t('actions.save')} type='primary' onClick={applyEdit} />
            </div>
          </>
        )}
        {!editing && <SchoolCard countries={countries} editTitle='common.edit' school={school} onEdit={editable ? () => setDraft(new School(school)) : null} />}
        <ConfirmableIconButton
          className='mt-12'
          confirmation={duplicate !== null ? t(labels.duplicate, { schoolName: duplicate[NAME] }) : null}
          disabled={disabled || editing || !isNamed || (!!selectedSchool && selectedSchool !== duplicate?.id)}
          icon={faSave}
          text={t(labels.validate)}
          type='primary'
          onClick={() => onValidate(school)}
        />
      </div>
      <div className='flex-column h-100 w-50'>
        <h3>{t(labels.description)}</h3>
        <SchoolsList
          excluded={schoolId}
          pinned={duplicate?.id}
          schools={schools}
          selected={selectedSchool}
          onSelectedChange={setSelectedSchool}
        />
        <IconButton
          className='mt-12'
          disabled={disabled || !selectedSchool}
          icon={faMerge}
          text={t(labels.merge)}
          type='primary'
          onClick={() => onMerge(schools.find(existing => existing.id === selectedSchool))}
        />
      </div>
    </div>
  )
}

export default SchoolValidator
